import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  updateProfile,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
} from 'firebase/auth';
import { auth, googleAuthProvider } from '../lib/firebase';
import { apiUrl } from '../utils/format';

export interface RoblokyAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  tag?: string;
  isAnonymous?: boolean;
}

interface AuthContextType {
  user: RoblokyAuthUser | FirebaseUser | null;
  idToken: string | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string, displayName: string, avatarUrl?: string) => Promise<void>;
  signInAsGuest: (displayName: string, avatarUrl?: string) => Promise<void>;
  updateUserAvatar: (avatarUrl: string) => Promise<void>;
  signOut: () => Promise<void>;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const LOCAL_AUTH_STORAGE_KEY = 'robloky_local_auth_session';

// Local SVG avatar so we never depend on external image hosts for defaults
const DEFAULT_AVATAR =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#06b6d4"/><stop offset="1" stop-color="#3b82f6"/></linearGradient></defs><rect width="150" height="150" fill="url(#g)"/><text x="75" y="97" font-family="monospace" font-size="64" font-weight="bold" fill="#0c101a" text-anchor="middle">N</text></svg>'
  );

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<RoblokyAuthUser | FirebaseUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Restore local session or listen to Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const token = await getIdToken(currentUser, true);
          setIdToken(token);
        } catch (err) {
          console.error('Failed to get user ID token:', err);
          setIdToken(null);
        }
        setLoading(false);
      } else {
        // Check if there is an active local/email authenticated session
        try {
          const savedSession = localStorage.getItem(LOCAL_AUTH_STORAGE_KEY);
          if (savedSession) {
            const parsed = JSON.parse(savedSession);
            if (parsed && parsed.user && parsed.token) {
              setUser(parsed.user);
              setIdToken(parsed.token);
            }
          }
        } catch (e) {
          console.warn('Failed to parse local auth session:', e);
        }
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleAuthProvider);
      const token = await getIdToken(result.user, true);
      setIdToken(token);
      setUser(result.user);
      localStorage.removeItem(LOCAL_AUTH_STORAGE_KEY);
    } catch (error: any) {
      console.error('Google Sign In Error:', error);
      throw error;
    }
  };

  const signInWithEmail = async (email: string, pass: string) => {
    const cleanEmail = email.trim().toLowerCase();
    // Email auth goes directly through our backend (Google endpoints are
    // unreachable from some regions and the SDK would fail with CORS/network
    // errors). The backend issues its own nx_auth token accepted by requireAuth.
    const res = await fetch(apiUrl('/robloky/api/auth/local-signin'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleanEmail, password: pass }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Failed to sign in.');
    }

    setUser(data.user);
    setIdToken(data.token);
    localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify({ user: data.user, token: data.token }));
  };

  const signUpWithEmail = async (
    email: string,
    pass: string,
    displayName: string,
    avatarUrl?: string
  ) => {
    const cleanEmail = email.trim().toLowerCase();
    // Register directly through our backend (same reason as sign-in above).
    // The endpoint creates the Cloud SQL profile and returns an nx_auth token.
    const res = await fetch(apiUrl('/robloky/api/auth/local-signup'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        password: pass,
        displayName: displayName || cleanEmail.split('@')[0] || 'RoblokyGamer',
        avatar: avatarUrl,
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Failed to complete registration.');
    }

    setUser(data.user);
    setIdToken(data.token);
    localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify({ user: data.user, token: data.token }));
  };

  const signInAsGuest = async (displayName: string, avatarUrl?: string) => {
    try {
      // Guest sessions are created directly on our backend (no Google round-trip).
      const guestEmail = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 6)}@robloky.local`;
      const res = await fetch(apiUrl('/robloky/api/auth/local-signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: guestEmail,
          displayName: displayName || 'GuestPlayer',
          avatar: avatarUrl,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to start guest session.');
      }
      setUser(data.user);
      setIdToken(data.token);
      localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify({ user: data.user, token: data.token }));
    } catch (error: any) {
      console.error('Guest Sign In Error:', error);
      throw error;
    }
  };

  const updateUserAvatar = async (avatarUrl: string) => {
    if (auth.currentUser) {
      try {
        await updateProfile(auth.currentUser, {
          photoURL: avatarUrl,
        });
        setUser({ ...auth.currentUser } as FirebaseUser);
      } catch (err) {
        console.error('Failed to update firebase auth photoURL:', err);
      }
    } else if (user) {
      const updated = { ...user, photoURL: avatarUrl };
      setUser(updated);
      if (idToken) {
        localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify({ user: updated, token: idToken }));
      }
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.warn('Firebase sign out notice:', error);
    }
    localStorage.removeItem(LOCAL_AUTH_STORAGE_KEY);
    setIdToken(null);
    setUser(null);
  };

  // Helper to make authenticated requests to our Cloud SQL backed Express API.
  // /api paths are rebased to the detected site prefix so calls survive
  // doubled/stale URL prefixes that would otherwise hit the SPA fallback.
  const authFetch = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers || {});
    if (idToken) {
      headers.set('Authorization', `Bearer ${idToken}`);
    }
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    return fetch(url.startsWith('/robloky/api/') ? apiUrl(url) : url, {
      ...options,
      headers,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        idToken,
        loading,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signInAsGuest,
        updateUserAvatar,
        signOut,
        authFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
